import { queryClient, queryKeys } from "@/client";
import generateSections from "@/components/DirectorySubmissions/Form/Sections";
import {
  type Section,
  type SubmissionFormData,
  type TableRow,
} from "@/components/DirectorySubmissions/Form/types";
import {
  saveMatterData,
  saveTableData,
} from "@/components/DirectorySubmissions/Form/utils";
import { createRevisionEntry } from "@/components/DirectorySubmissions/Form/utils/revisionUtils";
import { validateForm } from "@/components/DirectorySubmissions/Form/utils/validateForm";
import { type SupabaseClient } from "@supabase/supabase-js";
import { type EnqueueSnackbar } from "notistack";

export const tableMap: Record<string, string> = {
  A4: "directory_submission_contact_persons",
  B4: "directory_submission_department_heads",
  B5: "directory_submission_partner_changes",
  B6: "directory_submission_lawyer_info",
  C1: "directory_submission_barristers_used",
  PC: "directory_submission_publishable_clients",
  CC: "directory_submission_confidential_clients",
};

export const saveData = async ({
  supabase,
  firmId,
  userId,
  submissionId,
  enqueueSnackbar,
  formData,
  initialFormData,
  changedFields = [],
  sections = [],
  skipQueryInvalidation = false,
}: {
  supabase: SupabaseClient | undefined;
  firmId: string | undefined;
  userId: string | undefined;
  submissionId: number | undefined;
  enqueueSnackbar: EnqueueSnackbar;
  formData: SubmissionFormData;
  initialFormData: SubmissionFormData;
  changedFields?: string[];
  sections?: Section[];
  skipQueryInvalidation?: boolean;
}): Promise<Partial<SubmissionFormData>> => {
  if (!submissionId || !firmId || !userId || !supabase) {
    enqueueSnackbar("Failed to save changes, please refresh and try again.", {
      variant: "error",
      anchorOrigin: { horizontal: "right", vertical: "bottom" },
    });
    return {};
  }

  const updatedData: Partial<SubmissionFormData> = {};

  try {
    // If sections aren't provided or are empty, generate them
    let sectionsToUse = sections;
    if (!sections || !Array.isArray(sections) || sections.length === 0) {
      // Determine matter counts based on formData
      const publicMatterCount = Object.keys(formData).filter((key) =>
        key.startsWith("PM"),
      ).length;
      const confidentialMatterCount = Object.keys(formData).filter((key) =>
        key.startsWith("CM"),
      ).length;

      // Generate the sections using these counts
      sectionsToUse = generateSections(
        publicMatterCount,
        confidentialMatterCount,
      );
    }

    // Calculate invalid field count using the correct sections
    let invalidFieldCount = 0;
    if (sectionsToUse.length > 0) {
      const validationResult = validateForm(formData, sectionsToUse);
      invalidFieldCount = validationResult.invalidFieldCount;
    }

    // Update invalid_field_count in the database
    await supabase
      .from("directory_submission")
      .update({ invalid_field_count: invalidFieldCount })
      .eq("id", submissionId);

    // Don't add invalid_field_count to updatedData
    // This prevents it from appearing in diff calculations for unsaved changes
    // instead we'll handle updating initialFormData.invalid_field_count directly in the hook

    const overviewFields = {
      A1: "firm_name",
      A2: "practice_area",
      A3: "location",
      B1: "department_name",
      B2: "partners_count",
      B3: "other_lawyers_count",
      B7: "department_specialties",
      C2: "coverage_feedback",
    };

    for (const [key, dbField] of Object.entries(overviewFields)) {
      if (
        changedFields.includes(key) &&
        formData[key] !== initialFormData[key]
      ) {
        await supabase.from("directory_submission_overview").upsert(
          {
            [dbField]: formData[key],
            firm_id: firmId,
            directory_submission_id: submissionId,
          },
          {
            onConflict: "directory_submission_id",
          },
        );
        updatedData[key] = formData[key];
      }
    }

    for (const [key, table] of Object.entries(tableMap)) {
      if (
        changedFields.includes(key) &&
        JSON.stringify(formData[key]) !== JSON.stringify(initialFormData[key])
      ) {
        const updatedTableData = await saveTableData(
          table,
          submissionId,
          formData[key] as TableRow[],
          firmId,
          supabase,
        );
        updatedData[key] = updatedTableData;
      }
    }

    // Handle matters (both public and confidential)
    const changedPMMatters: Record<string, Record<string, any>> = {};
    const changedCMMatters: Record<string, Record<string, any>> = {};

    changedFields.forEach((field) => {
      if (field.startsWith("PM") && field !== "PC") {
        changedPMMatters[field] = formData[field] as Record<string, any>;
      } else if (field.startsWith("CM") && field !== "CC") {
        changedCMMatters[field] = formData[field] as Record<string, any>;
      }
    });

    if (Object.keys(changedPMMatters).length > 0) {
      const updatedPMMatters = await saveMatterData(
        "PM",
        submissionId,
        changedPMMatters,
        firmId,
        userId,
        supabase,
      );
      Object.assign(updatedData, updatedPMMatters);
    }
    if (Object.keys(changedCMMatters).length > 0) {
      const updatedCMMatters = await saveMatterData(
        "CM",
        submissionId,
        changedCMMatters,
        firmId,
        userId,
        supabase,
      );
      Object.assign(updatedData, updatedCMMatters);
    }

    const isTableField = (field: string): boolean => {
      const value = formData[field];
      if (!Array.isArray(value) || value.length === 0) return false;
      const firstRow = value[0] as TableRow;
      return (
        typeof firstRow === "object" && firstRow !== null && "value" in firstRow
      );
    };

    for (const field of changedFields) {
      // Process table fields cell‑by‑cell
      if (isTableField(field)) {
        const prevTable = (initialFormData[field] as TableRow[]) || [];
        const newTable = (formData[field] as TableRow[]) || [];
        const rowCount = Math.max(prevTable.length, newTable.length);
        for (let i = 0; i < rowCount; i++) {
          // Get the row values or an empty array if the row is missing.
          const prevRow = prevTable[i]?.value || [];
          const newRow = newTable[i]?.value || [];
          const colCount = Math.max(prevRow.length, newRow.length);
          for (let j = 0; j < colCount; j++) {
            const prevValue = prevRow[j] === undefined ? "" : prevRow[j];
            const newValue = newRow[j] === undefined ? "" : newRow[j];
            if (prevValue !== newValue) {
              await createRevisionEntry({
                submissionId: submissionId.toString(),
                questionId: field,
                formData,
                previousValue: prevValue,
                newValue,
                userId,
                firmId,
                supabase,
                rowIndex: i,
                columnIndex: j,
              });
            }
          }
        }
      } else {
        if (formData[field] !== initialFormData[field]) {
          await createRevisionEntry({
            submissionId: submissionId.toString(),
            questionId: field,
            formData,
            previousValue: initialFormData[field],
            newValue: formData[field],
            userId,
            firmId,
            supabase,
          });
        }
      }
    }

    // Only invalidate queries if specifically requested (default is true)
    if (!skipQueryInvalidation) {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.directorySubmissionRevisions(`${submissionId}`),
      });

      void queryClient.invalidateQueries({
        queryKey: queryKeys.directorySubmission(`${submissionId}`),
      });
    }

    return updatedData;
  } catch (error) {
    console.error("Error saving data:", error);
    enqueueSnackbar("Failed to save changes, please refresh and try again.", {
      variant: "error",
      anchorOrigin: { horizontal: "right", vertical: "bottom" },
    });

    return {};
  }
};
